// src/com/auto_plan_upload/AutoPlanUpload.js
import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/system';
import { styled } from '@mui/material/styles';
import { Button, IconButton, CircularProgress } from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { saveAs } from 'file-saver';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';
import UploadIcon from '@mui/icons-material/Upload'; // 배차판 업로드
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest'; // 자동 배차 생성.
import HubIcon from '@mui/icons-material/Hub'; // 동적 배차 생성.
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep'; // 동적 일괄 삭제.
import RecommendIcon from '@mui/icons-material/Recommend'; // 배차 확정
import BarChartIcon from '@mui/icons-material/BarChart'; // 배차 차종 통계
import DownloadIcon from '@mui/icons-material/Download'; // 자동 배차 다운로드
import CachedIcon from '@mui/icons-material/Cached'; // refresh
import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client';
import { useSnackbar } from 'notistack';
import {
    ContentConainer, ContentHeader, ContentBody, ControlBox,
    CountLabel, ToolBox, FullsizeWaiting
} from '../../common/TopViewControl';
import { useConfirmDialog } from '../../common/AppConfirmDialog';
import { AppObject, AppStyle, AppWord } from '../../value/AppConst';
import {
    arriveInfoListToSelectStoreRepo,
    authTableTreeRepo, autoPlanStateHeaderRepo, autoPlanUploadListParamRepo,
    autoPlanUploadListRepo, transPlanChangedRepo, userInfoRepo,
    vehTypeGroupOrderListByDcRepo
} from '../../value/AppModel';
import SearchBox from '../../common/SearchBox';
import { AUTO_PLAN_ALL_STATE } from './AutoPlanUploadGql';
import {
    useAlertSetter, useAuthorizationCheck, useClientSize
} from '../../api/app-hook';
import ValueMisc from '../../value/ValueMisc';
import AutoPlanUploadRow from './AutoPlanUploadRow';
import AutoPlanUploadUploader from './AutoPlanUploadUploader';
import { BUILD_AUTO_PLAN } from '../auto_plan/AutoPlanGql';
import {
    BUILD_AUTO_PLAN_BIND, CLEAR_AUTO_PLAN_BIND
} from '../auto_plan_bind/AutoPlanBindGql';
import {
    GET_AUTO_CPP_INFO_STATE_HEADER, STEP_UP_AUTO_CPP_INFO
} from '../auto_cpp_info/AutoCppInfoGql';
import AutoPlanUploadExcel from './AutoPlanUploadExcel';
import AutoRotationHis from '../auto_rotation_his/AutoRotationHis';
import {
    LIST_VEH_TYPE_GROUP_ORDER_BY_DC
} from '../veh_type_group_order/VehTypeGroupOrderGql';
import { LIST_ARRIVE_INFO_TO_SELECT_STORE } from '../arrive_info/ArriveInfoGql';

const BorderLightBlue = AppStyle.BorderCCC;

const StateBox = styled(Box)({
    display: 'flex',
    border: AppStyle.BorderCCC,
    borderRadius: 5, width: '100%',
    fontSize: '0.85rem',
});

const StateCol = styled(Box)({flexGrow: 1});
const StateHead = styled(Box)({
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    height: 30, backgroundColor: '#f2f2f2', fontSize: '0.75rem',
    borderBottom: AppStyle.BorderCCC
});
const StateCell = styled(Box)({
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    height: 30
});

const PlanTableContainer = styled('div')({
    position:'absolute', top:0, left:0, right:0, bottom:0, overflowY:'auto'
});

const PlanTable = styled('table')({
    width: '100%',
    borderCollapse: 'collapse'
});
const PlanTableHead = styled('thead')({
    position: 'sticky',
    top: 0,
    backgroundColor: '#f2f2f2',
    border: AppStyle.BorderCCC,
    boxSizing: 'border-box',
    zIndex: 1
});
const StoreRow = styled('tr')({
    border: AppStyle.BorderCCC,
    padding: 0,
});
const StoreTableBody = styled('tbody')({
    padding: 0,
    fontSize: '0.75rem',
    lineHeight: '1.2rem',
});
const TitleCell = styled('td')({
    fontSize: '0.75rem',
    textAlign: 'center',
    padding: 5,
    lineHeight: '1.35rem',
    boxSizing: 'border-box',
});

const EmptyCppInfoStateHeaderItem = {
    dcName: "",
    dcCd: "",
    dayTotVol: 0,
    cppTot: 0,
    tpc: 0,
    fillRate: 0,
    leftOver: 0,
    processStep: null,
    vehTotCnt: 0,
    updateDate: "",
    regDate: "",
    tpcTarget: 0
};

/**
 * (AUTO_PLAN_UPLOAD) 정보 View Controller
 * 자동배차 준비 정보
 * 자동배차, 업로드, 동적 등을 조합하여 출력하고
 * 업로드, 배차, 동적생성 등을 수행하기 위한 종합 제어 화면.
 * Using React with MUI5
 * Project: NewTOP for cloud
 * 2025-03-13 13:29:08 using DbdbTap
 * https://www.dbdbtap.com
 * 모든 레코드를 다 출력하면 매우 느려짐. 스크롤에 따라 출력할 레코드를 조절하고 다른 레코드들은 빈 열을 출력. (range)
 */
export default function AutoPlanUpload() {
    const sessionInfo = useReactiveVar(userInfoRepo);
    const authTableTree = useReactiveVar(authTableTreeRepo);
    const autoPlanUploadListData = useReactiveVar(autoPlanUploadListRepo); // 점포별로 하나의 레코드.
    const arriveInfoListToSelectStoreData = useReactiveVar(arriveInfoListToSelectStoreRepo);
    const transPlanChanged = useReactiveVar(transPlanChangedRepo);
    const [uploadListFiltered, setUploadListFiltered] = useState([]);
    const autoPlanUploadListParam = useReactiveVar(autoPlanUploadListParamRepo);
    //const autoPlanBindListData = useReactiveVar(autoPlanBindListRepo); // 동적.
    //const autoPlanListData = useReactiveVar(autoPlanListRepo); // 배차내용
    const autoPlanStateHeader = useReactiveVar(autoPlanStateHeaderRepo);
    const [autoPlanListCount, setAutoPlanListCount] = useState(0);
    const [scrollPos, setScrollPos] = useState({top:0, height:800, scrollHeight:800});
    const [countToDisplay, setCountToDisplay] = useState(0);
    const [searchText, setSearchText] = useState('');
    const [openUploader, setOpenUploader] = useState(false);
    const [autoRotationHisState, setAutoRotationHisState] = useState({open:false, planDate: null});
    const vehTypeGroupOrderListByDc = useReactiveVar(vehTypeGroupOrderListByDcRepo);
    const openConfirmDialog = useConfirmDialog();
    const checkAuth = useAuthorizationCheck();
    const [range, setRange] = useState({start:0, end:20}); // 출력 느린 것 개선하기 위해.
    const clientSize = useClientSize();
    const contentRef = useRef(null);
    const addAlert = useAlertSetter();
    const { enqueueSnackbar } = useSnackbar();
    const lastReloadRef = useRef(0);
    const skipInitTransPlanChangedRef = useRef(true); // 초기화 때에는 transPlanChanged의 영향 받지 않도록 한다.

    // ##### Call GraphQL to get List #####
    const [getAutoPlanUploadAllState, responseState] = useLazyQuery(AUTO_PLAN_ALL_STATE, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetList(data, option)},
		onError: (error) => addAlert({title: "배차정보 조회 오류", message: [error.message]})
    });
    const [getVehTypeGroupOrderList, responseVehTypeList] = useLazyQuery(LIST_VEH_TYPE_GROUP_ORDER_BY_DC, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetVehTypeList(data, option)},
        onError: (error) => addAlert({title: "차종목록 조회 오류", message: [error.message]})
    });
    // Get store list from arrive_info table.
    const [getArriveInfoList, responseStoreList] = useLazyQuery(LIST_ARRIVE_INFO_TO_SELECT_STORE, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetStoreList(data, option)},
        onError: (error) => addAlert({title: '도착지 목록 조회 오류', message: [error.message]})
    });

    const [buildAutoPlan, buildResponseState] = useMutation(BUILD_AUTO_PLAN, {
        onCompleted: (data, option) => { onCompleteBuildAutoPlan(data, option) },
        onError: (error) => addAlert({title: "자동배차 생성 오류", message: [error.message]})
    });

    const [buildAutoPlanBind, buildBindResponseState] = useMutation(BUILD_AUTO_PLAN_BIND, {
        onCompleted: (data, option) => { onCompleteBuildAutoPlanBind(data, option) },
        onError: (error) => addAlert({title: "동적 배차 생성 오류", message: [error.message]})
    });

    const [clearAutoPlanBind, clearBindResponseState] = useMutation(CLEAR_AUTO_PLAN_BIND, {
        onCompleted: (data, option) => { onCompleteClearAutoPlanBind(data, option) },
        onError: (error) => addAlert({title: "동적 일괄 삭제 오류", message: [error.message]})
    });

    const [stepUpAutoCppInfo, stepUpResponseState] = useMutation(STEP_UP_AUTO_CPP_INFO, {
        onCompleted: (data, option) => { onCompleteStepUpAutoCppInfo(data, option) },
        onError: (error) => addAlert({title: "배차 단계 변경 오류", message: [error.message]})
    });

    const [getAutoCppInfoStateHeader, autoCppInfoStateHeaderResponse] = useLazyQuery(GET_AUTO_CPP_INFO_STATE_HEADER, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => { onCompleteGetAutoCppInfoStateHeader(data, option) },
        onError: (error) => addAlert({title: "헤더 데이터 조회 오류", message: [error.message]})
    });

    const getAutoPlanUploadListNow = (planDate) => {
        const param = {variables:{planDate:planDate}};
        getAutoPlanUploadAllState(param);
        lastReloadRef.current = new Date().getTime()/1000;
    };

    useEffect(() => {
        if(autoPlanUploadListParam.planDate) {
            getAutoPlanUploadListNow(autoPlanUploadListParam.planDate);
        }
        else {
            const newParam = {...autoPlanUploadListParam};
            newParam.planDate = ValueMisc.getYMD();
            autoPlanUploadListParamRepo(newParam);
        }
    }, [autoPlanUploadListParam]);

    // trans_plan 테이블에 추가 또는 삭제가 있을 경우 auto_plan 등에도 반영되므로 다시 읽는다.
    useEffect(() => {
        if(skipInitTransPlanChangedRef.current) {
            skipInitTransPlanChangedRef.current = false;
            return;
        }
        if(transPlanChanged.planDate === autoPlanUploadListParam.planDate) {
            getAutoPlanUploadListNow(autoPlanUploadListParam.planDate);
        }
    }, [transPlanChanged]);

    useEffect(() => {
            const now = new Date().getTime() / 1000;

            // 차종목록
            let diff = now - vehTypeGroupOrderListByDc.when;
            if(diff > 300) {
                getVehTypeGroupOrderList({variables:{dcCd:sessionInfo.getDcCd()}});
            }
    }, [vehTypeGroupOrderListByDc, sessionInfo]);

    useEffect(() => {
        const now = new Date().getTime() / 1000;
        let diff = now - arriveInfoListToSelectStoreData.when;
        if(diff > 300) {
            getArriveInfoList({variables:{dcCd:sessionInfo.getDcCd()}});
        }
    }, [arriveInfoListToSelectStoreData]);

    useEffect(() => {
        let list;
        if(searchText && searchText.length > 0) {
            list = autoPlanUploadListData.records.filter((item)=>{
                    return item.storeName.includes(searchText) || item.storeId.includes(searchText);
                });
        }
        else {
            list = autoPlanUploadListData.records;
        }
        setUploadListFiltered(list);
        setCountToDisplay(list.length);
    }, [autoPlanUploadListData.records, searchText]);

    // Check which records are in the visible area.
    useEffect(() => {
        if(contentRef.current) {
            const height = contentRef.current.clientHeight;
            const newData = {...scrollPos};
            newData.height = height;
            setScrollPos(newData);
        }
    }, [clientSize]);

    // to setRange
    useEffect(()=>{
        if(scrollPos.scrollHeight > 0 && scrollPos.height > 0) {
            //  recordHeight 값은 보통 101 정도.
            let recordHeight = countToDisplay > 0 ? Math.floor(scrollPos.scrollHeight / countToDisplay) : 90;
            if(recordHeight < 90) recordHeight = 90; // 초기화 돌 때에는 scrollHeight가 맞는 값이 아니므로..
            const firstRecordToSHow = Math.floor(scrollPos.top / recordHeight);
            const lastRecordToShow = Math.floor((scrollPos.top + scrollPos.height) / recordHeight) + 1;
            setRange({start:firstRecordToSHow, end:lastRecordToShow});
        }
    }, [scrollPos, countToDisplay]);

    // >>>>>>>>> callbacks <<<<<<<<<<<<<
    const onCompleteGetList = (data, clientOption) => {
        if(
            data.autoPlanUploadList
            && data.autoPlanList
            && data.autoPlanBindList
        ) {
            // autoPlanUploadList iteration에 동적정보를 찾아 넣기 위해 맵을 만든다.
            const bindOneMap = {}; // bindIndex=1
            const bindTwoMap = {}; // bindIndex=2

            // dcCd, planDate로 검색한 동적정보를 맵으로.
            // 레거시 코드에 따라서 동일한 동적정보가 targetStoreId와 bindStoreId아 같은
            // storeId를 가지는 레코드에 모두 나타나게.
            data.autoPlanBindList.forEach((bind) => {
                if(bind.bindIndex === 1) {
                    bindOneMap[bind.targetStoreId] = bind;
                    bindOneMap[bind.bindStoreId] = bind;
                }
                if(bind.bindIndex === 2) {
                    bindTwoMap[bind.targetStoreId] = bind;
                    bindTwoMap[bind.bindStoreId] = bind;
                }
            });

            // autoPlanList iteration에 배차정보를 찾아 넣기 위해 맵을 만든다.
            // auto_plan primary key (dc_cd, plan_date, arrive_id, hocha), arrive_id := store_id
            const planMap = {};
            data.autoPlanList.forEach((plan) => {
                const storeId = plan.storeId;
                if(!planMap[storeId]) planMap[storeId] = [];
                planMap[storeId].push(plan);
            });
            setAutoPlanListCount(data.autoPlanList.length);

            const records = [];
            data.autoPlanUploadList.forEach((item) => {
                const storeId = item.storeId;
                if(bindOneMap[storeId]) {
                    item.bindOne = bindOneMap[storeId];
                }
                if(bindTwoMap[storeId]) {
                    item.bindTwo = bindTwoMap[storeId];
                }
                if(planMap[storeId]) {
                    item.plans = planMap[storeId];
                }
                else item.plans = [];
                records.push(item);
            });
            setCountToDisplay(records.length);
            autoPlanUploadListRepo({
                when: new Date().getTime()/1000,
                records: records
            });
        }
        else {
            if(!data.autoPlanUploadList) {
                addAlert({
                    title: '점포 별 배차 업로드 정보(메인 데이터)',
                    message: ['점포 별 배차 업로드 정보 조회 오류']
                });
            }
            if(!data.autoPlanList) {
                addAlert({
                    title: '배차내용',
                    message: ['배차내용 조회 오류']
                });
            }
            if(!data.autoPlanBindList) {
                addAlert({
                    title: '배차 동적 정보',
                    message: ['동적 정보 조회 오류']
                });
            }
        }

        if(data.autoCppInfoStateHeaderItem) {
            autoPlanStateHeaderRepo({
                when: new Date().getTime()/1000,
                data: data.autoCppInfoStateHeaderItem
            });
        }
        else {
            /* addAlert({
                title: '출고예정수량',
                message: ['출고예정수량이 등록되어 있지 않습니다.']
            }); */
            autoPlanStateHeaderRepo({
                when: new Date().getTime()/1000,
                data: EmptyCppInfoStateHeaderItem
            });
        }
    };

    const onCompleteGetAutoCppInfoStateHeader = (data, clientOption) => {
        if(data.autoCppInfoStateHeaderItem) {
            autoPlanStateHeaderRepo({
                when: new Date().getTime()/1000,
                data: data.autoCppInfoStateHeaderItem
            });
        }
        else {
            addAlert({
                title: '헤더 데이터',
                message: ['헤더 데이터를 조회하지 못했습니다.']
            });
        }
    };
        
    const onCompleteGetVehTypeList = (data, clientOption) => {
        if(data.vehTypeGroupOrderListByDc) {
            vehTypeGroupOrderListByDcRepo({when:new Date().getTime()/1000, records:data.vehTypeGroupOrderListByDc});
        }
    };
    
    const onCompleteGetStoreList = (data, clientOption) => {
        if(data.arriveInfoList) {
            if(data.arriveInfoList.length > 0) {
                // sort by storeId (text)
                data.arriveInfoList
                .filter((item) => item.useYn === 'Y' && item.autoPlanYn === 'Y')
                .sort((a,b) => {
                    if(a.storeId < b.storeId) return -1;
                    if(a.storeId > b.storeId) return 1;
                    return 0;
                });
            }
            arriveInfoListToSelectStoreRepo({
                when:new Date().getTime()/1000,
                records:data.arriveInfoList
            });
        }
    };

    const onCompleteBuildAutoPlan = (data, clientOption) => {
        if(data.autoPlanBuild.ok) {
            enqueueSnackbar("자동 배차 생성 완료", {variant:'success', autoHideDuration:1500});
            getAutoPlanUploadListNow(autoPlanUploadListParam.planDate);
        }
        else addAlert({title:"자동배차 생성", message: [data.autoPlanBuild.message]});
    };

    const onCompleteBuildAutoPlanBind = (data, clientOption) => {
        if(data.autoPlanBindBuild.ok) {
            enqueueSnackbar("동적 배차 작업 완료", {variant:'success', autoHideDuration:1500});
            getAutoPlanUploadListNow(autoPlanUploadListParam.planDate);
        }
        else addAlert({title:"동적 배차 생성", message: [data.autoPlanBindBuild.message]});
    };

    const onCompleteClearAutoPlanBind = (data, clientOption) => {
        if(data.autoPlanBindClear.ok) {
            enqueueSnackbar("동적 일괄 삭제 완료", {variant:'success', autoHideDuration:1500});
            getAutoPlanUploadListNow(autoPlanUploadListParam.planDate);
        }
        else addAlert({title:"동적 일괄 삭제", message: [data.autoPlanBindClear.message]});
    };

    const onCompleteStepUpAutoCppInfo = (data, clientOption) => {
        if(data.autoCppInfoStepUp.ok) {
            enqueueSnackbar("배차 단계 변경 완료", {variant:'success', autoHideDuration:1500});
            //getAutoPlanUploadListNow(autoPlanUploadListParam.planDate);
            getAutoCppInfoStateHeader({
                variables: {planDate: autoPlanUploadListParam.planDate}
            });
        }
        else addAlert({title:"배차 단계 변경", message: [data.autoCppInfoStepUp.message]});
    };

    // >>>>>>>>> Event Handler <<<<<<<<<<<<<

    const onContentScroll = (e) => {
        const top = e.target.scrollTop;
        const height = e.target.clientHeight;
        const scrollHeight = e.target.scrollHeight;
        setScrollPos({top:top, height:height, scrollHeight:scrollHeight});
    };

    const onClickReload = () => {
        const diff = new Date().getTime()/1000 - lastReloadRef.current;
        if(diff < 3) {
            // Too short to reload
            enqueueSnackbar(AppWord.ReloadNotIn3Sec, {variant:'info', autoHideDuration:1500});
        }
        else getAutoPlanUploadListNow(autoPlanUploadListParam.planDate);
    };

    const onChangePlanDate = (date) => {
        const ymd = ValueMisc.getYMD(date.toDate());
        if(ymd !== autoPlanUploadListParam.planDate) {
            const newParam = {...autoPlanUploadListParam};
            newParam.planDate = ymd;
            autoPlanUploadListParamRepo(newParam);
            getAutoPlanUploadListNow(ymd);
        }
    };

    const onChangeSearchText = (text) => {
        setSearchText(text);
    };

    const onClickOpenUploader = () => {
        setOpenUploader(true); // 없어도 그냥 추가하도록 되어있음(과거 오라클)
        /* if(autoPlanStateHeader.data.processStep) setOpenUploader(true);
        else addAlert({
            title: '출고예정수량',
            message: ['출고예정수량이 등록되어 있지 않습니다.']
        }); */
    };

    const onCloseUploader = (uploaded) => {
        setOpenUploader(false);
        if(uploaded) {
            // getTransPlanTsrListUsingParam();
            getAutoPlanUploadListNow(autoPlanUploadListParam.planDate);
        }
    };

    const askAndBuildAutoPlan = async () => {
        const confirm =  await openConfirmDialog({
            title: "자동 배차 생성",
            content: [
                '기존 배차정보, 동적배차정보 등이 있는 경우 모두 삭제됩니다.',
                '계속하시겠습니까?'
            ]
        });

        if(confirm) {
            const param = {
                planDate: autoPlanUploadListParam.planDate
            };
            buildAutoPlan({variables:{autoPlan:param}});
        }
    };

    const onClickBuildAutoPlan = () => {
        askAndBuildAutoPlan();
    };

    const askAndBuildAutoPlanBind = async () => {
        const confirm =  await openConfirmDialog({
            title: "동적 배차 생성",
            content: [
                '동적 배차정보를 생성합니다.',
                '계속하시겠습니까?'
            ]
        });

        if(confirm) {
            const param = {
                planDate: autoPlanUploadListParam.planDate
            };
            buildAutoPlanBind({variables:{autoPlanBind:param}});
        }
    };

    const onClickBuildAutoPlanBind = () => {
        askAndBuildAutoPlanBind();
    };

    const askAndClearAutoPlanBind = async () => {
        const confirm =  await openConfirmDialog({
            title: "동적 배차 일괄 삭제",
            content: [
                '동적 배차 내용을 일괄 삭제합니다.',
                '계속하시겠습니까?'
            ]
        });

        if(confirm) {
            const param = {
                planDate: autoPlanUploadListParam.planDate
            };
            clearAutoPlanBind({variables:{autoPlanBind:param}});
        }
    };

    const onClickClearAutoPlanBind = () => {
        askAndClearAutoPlanBind();
    };

    const askAndStepUpAutoCppInfo = async () => {
        const confirm =  await openConfirmDialog({
            title: "배차 확정",
            content: [
                '배차 확정 후에는 이전 상태로 돌아갈 수 없습니다.',
                '계속하시겠습니까?'
            ]
        });

        if(confirm) {
            const param = {
                planDate: autoPlanUploadListParam.planDate
            };
            stepUpAutoCppInfo({variables:{autoCppInfo:param}});
        }
    };

    const canStepUp = checkAuth(authTableTree.tree.pr_auto_cpp_info_step_up);

    const onClickStepUpAutoCppInfo = () => {
        askAndStepUpAutoCppInfo();
    };

    const onClickSaveExcel = async () => {
        const excelBuilder = new AutoPlanUploadExcel(autoPlanUploadListData.records);
        const blob =  await excelBuilder.createExcel();
        const ymd = autoPlanUploadListParam.planDate.replace(/\D/g, '');
        
        saveAs(blob, 'autoplan_' + sessionInfo.user.dscIdAs + '_' + ymd + '.xlsx');
    };

    const onClickPopAutoRotationHis = () => {
        setAutoRotationHisState({open:true, planDate:autoPlanUploadListParam.planDate});
    };

    const onRequestCloseAutoRotationHis = () => {
        setAutoRotationHisState({open:false, planDate:null});
    }

    const onRequestReload = () => {
        getAutoPlanUploadListNow(autoPlanUploadListParam.planDate);
    };

    const loadingAny
        = responseState.loading
        || buildResponseState.loading
        || buildBindResponseState.loading
        || clearBindResponseState.loading
        || stepUpResponseState.loading
        || autoCppInfoStateHeaderResponse.loading
        || responseVehTypeList.loading
        || responseStoreList.loading;

    return(
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ko">
            <ContentConainer ref={contentRef}>
                <ContentHeader>
                    <ControlBox>
                        <DatePicker label={'배차 날짜'}
                            sx={{width:150}}
                            slotProps={{
                                openPickerIcon:{fontSize:AppWord.SMALL},
                                textField:{size:AppWord.SMALL}
                            }}
                            format='YYYY-MM-DD'
                            value={
                                autoPlanUploadListParam.planDate ?
                                dayjs(autoPlanUploadListParam.planDate) :
                                dayjs()
                            }
                            onChange={onChangePlanDate}
                            disabled={loadingAny}
                        />
                    </ControlBox>
                    <ToolBox>
                        <Button
                            variant={AppStyle.mui.contained}
                            color={AppStyle.mui.inherit}
                            startIcon={<UploadIcon />}
                            disabled={loadingAny}
                            onClick={onClickOpenUploader}
                        >
                            배차판 업로드
                        </Button>
                        <Button
                            variant={AppStyle.mui.contained}
                            color={AppStyle.mui.primary}
                            startIcon={<SettingsSuggestIcon />}
                            style={{marginLeft:5}}
                            onClick={onClickBuildAutoPlan}
                            disabled={loadingAny}
                        >
                            자동 배차 생성
                        </Button>
                        <Button
                            variant={AppStyle.mui.contained}
                            color={AppStyle.mui.success}
                            startIcon={<HubIcon />}
                            style={{marginLeft:5}}
                            disabled={loadingAny}
                            onClick={onClickBuildAutoPlanBind}
                        >
                            동적 배차 생성
                        </Button>
                        <Button
                            variant={AppStyle.mui.contained}
                            color={AppStyle.mui.warning}
                            startIcon={<DeleteSweepIcon />}
                            style={{marginLeft:5}}
                            disabled={loadingAny}
                            onClick={onClickClearAutoPlanBind}
                        >
                            동적 일괄 삭제
                        </Button>
                    </ToolBox>
                </ContentHeader>
                <ContentHeader>
                    {/* 상온 자동배차 상황판 */}
                    <StateBox>
                        <StateCol>
                            <StateHead>배차단계</StateHead>
                            <StateCell style={{color:'red', fontWeight:'bold'}}>
                                {autoPlanStateHeader.data.processStep || 'Wait..'}
                            </StateCell>
                        </StateCol>
                        <StateCol>
                            <StateHead>당일 예상 Volume</StateHead>
                            <StateCell>{ValueMisc.float2str(autoPlanStateHeader.data.dayTotVol)}</StateCell>
                        </StateCol>
                        <StateCol>
                            <StateHead>배차대수</StateHead>
                            <StateCell>{ValueMisc.float2str(autoPlanStateHeader.data.vehTotCnt)}</StateCell>
                        </StateCol>
                        <StateCol>
                            <StateHead>TPC타겟</StateHead>
                            <StateCell>{autoPlanStateHeader.data.tpcTarget}</StateCell>
                        </StateCol>
                        <StateCol>
                            <StateHead>TPC</StateHead>
                            <StateCell>{autoPlanStateHeader.data.tpc}</StateCell>
                        </StateCol>
                        <StateCol>
                            <StateHead>CPP</StateHead>
                            <StateCell>{autoPlanStateHeader.data.tpc}</StateCell>
                        </StateCol>
                        <StateCol>
                            <StateHead>Fill rate</StateHead>
                            <StateCell>{autoPlanStateHeader.data.fillRate}</StateCell>
                        </StateCol>
                        <StateCol>
                            <StateHead>Left Over</StateHead>
                            <StateCell>{autoPlanStateHeader.data.leftOver}</StateCell>
                        </StateCol>
                    </StateBox>
                </ContentHeader>
                <ContentHeader>
                    <ControlBox>
                        <SearchBox
                            label="점포 검색"
                            value={searchText}
                            onChangeText={onChangeSearchText}
                        />
                    </ControlBox>
                    <ToolBox>
                        <CountLabel count={countToDisplay} />
                        {
                            loadingAny ?
                            <CircularProgress size={20} />
                            :
                            <IconButton
                                color={AppStyle.mui.inherit}
                                onClick={onClickReload}
                                disabled={loadingAny}
                            >
                                <CachedIcon />
                            </IconButton>
                        }
                        <Button
                            variant={AppStyle.mui.contained}
                            color={AppStyle.mui.primary}
                            startIcon={<RecommendIcon />}
                            style={{marginLeft:5}}
                            disabled={
                                loadingAny || autoPlanStateHeader.data.processStep==='F'
                                || !canStepUp
                            }
                            onClick={onClickStepUpAutoCppInfo}
                        >
                            배차 확정
                        </Button>
                        <Button
                            variant={AppStyle.mui.contained}
                            color={AppStyle.mui.info}
                            startIcon={<BarChartIcon />}
                            style={{marginLeft:5}}
                            disabled={loadingAny}
                            onClick={onClickPopAutoRotationHis}
                        >
                            배차 차종 통계
                        </Button>
                        <Button
                            variant={AppStyle.mui.contained}
                            color={AppStyle.mui.inherit}
                            startIcon={<DownloadIcon />}
                            style={{marginLeft:5}}
                            disabled={loadingAny}
                            onClick={onClickSaveExcel}
                        >
                            자동 배차 다운로드
                        </Button>
                    </ToolBox>
                </ContentHeader>
                <ContentBody style={{position:'relative'}}>
                    <PlanTableContainer style={{position:'absolute', top:0, left:0, right:0, bottom:0}}
                        onScroll={onContentScroll}
                    >
                        <PlanTable cellSpacing={0}>
                            <PlanTableHead>
                                <StoreRow>
                                    <TitleCell>Dock<br/>No.</TitleCell>
                                    <TitleCell
                                        colSpan={2}
                                    >
                                        <Box
                                            style={{width:'100%', display:'flex', flexDirection:'column'}}
                                        >
                                            <Box style={{borderBottom: BorderLightBlue}}>
                                                Dest Info.
                                            </Box>
                                            <Box
                                                style={{display:'flex'}}
                                            >
                                                <Box style={{
                                                    flexGrow:9,display:'flex',
                                                    alignItems:'center',justifyContent:'center'
                                                    }}
                                                >점포명</Box>
                                                <Box style={{
                                                    flexGrow:6,display:'flex',
                                                    alignItems:'center',justifyContent:'center'
                                                    }}
                                                >주력/최대</Box>
                                            </Box>
                                        </Box>
                                        
                                    </TitleCell>
                                    <TitleCell>거리<br/>시간</TitleCell>
                                    <TitleCell>LO</TitleCell>
                                    <TitleCell>Total<br/>Vol.</TitleCell>
                                    <TitleCell>N/Y/O</TitleCell>
                                    <TitleCell>
                                        PBS_ PT15<br/>
                                        점포별 CPP
                                    </TitleCell>
                                    <TitleCell>당일PT마감</TitleCell>
                                    <TitleCell
                                        style={{
                                            borderRight:BorderLightBlue
                                        }}
                                    >
                                        당일PT야간<br/>
                                        당일PT오전<br/>
                                        당일PT오후
                                    </TitleCell>
                                    <TitleCell
                                        style={{
                                            borderRight:BorderLightBlue,
                                            minWidth:50
                                        }}
                                    >
                                        동적1
                                    </TitleCell>
                                    <TitleCell
                                        style={{
                                            borderRight:BorderLightBlue,
                                            minWidth:50
                                        }}
                                    >
                                        동적2
                                    </TitleCell>
                                    <TitleCell>
                                        운 송 사<br/>
                                        상차공간<br/>
                                        DIDA
                                    </TitleCell>
                                    <TitleCell>
                                        전체 PT<br/>
                                        미출고PT<br/>
                                        20-04PT
                                    </TitleCell>
                                    <TitleCell>호차</TitleCell>
                                    {
                                        Array(10).fill(0).map((item, index) => {
                                            return(
                                                <TitleCell key={index}>
                                                    {index+1}호
                                                </TitleCell>
                                            );
                                        })
                                    }
                                    <TitleCell style={{minWidth:80}}>비고</TitleCell>
                                </StoreRow>
                            </PlanTableHead>
                            <StoreTableBody>
                                {
                                    /* 점포별 배차 내용 */
                                    uploadListFiltered.map((item, idx) => <AutoPlanUploadRow
                                            key={item.arriveId}
                                            onRequestReload={onRequestReload}
                                            canDeliver={autoPlanListCount > 0}
                                            item={idx < range.start || idx >= range.end ? null : item}
                                        />)
                                }
                            </StoreTableBody>
                        </PlanTable>
                    </PlanTableContainer>
                </ContentBody>
            </ContentConainer>
            <AutoPlanUploadUploader
                open={openUploader}
                onClose={onCloseUploader}
            />
            {buildResponseState.loading && <FullsizeWaiting message="자동 배차 생성 중..." />}
            {buildBindResponseState.loading && <FullsizeWaiting message="동적 배차 생성 중..." />}
            {clearBindResponseState.loading && <FullsizeWaiting message="동적 일괄 삭제 중..." />}
            <AutoRotationHis
                open={autoRotationHisState.open}
                onClose={onRequestCloseAutoRotationHis}
                planDate={autoRotationHisState.planDate}
            />
        </LocalizationProvider>
    );
}
